import { createContext, useState, FC, ReactNode, useContext, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import { getAllUsage } from "../utilities/api/adminService";

interface UsageAnalyticsContextValue {
  usageData: any[];
  totalPages: number;
  currentPage: number;
  perPage: number;
  totalItems: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  loading: boolean;
  fetchUsageData: (page?: number) => Promise<void>;
  setPerPage: (perPage: number) => void;
}

export const UsageAnalyticsContext = createContext<UsageAnalyticsContextValue>({
  usageData: [],
  totalPages: 1,
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  hasPreviousPage: false,
  hasNextPage: false,
  loading: false,
  fetchUsageData: async () => {},
  setPerPage: () => {},
});

export const UsageAnalyticsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { accessToken } = useContext(AuthContext);
  const [usageData, setUsageData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchUsageData = useCallback(
    async (page: number = 1) => {
      if (!accessToken) return;
      try {
        setLoading(true);
        const response = await getAllUsage(accessToken, page, perPage);
        if (response.ok) {
          setUsageData(response.data.data);
          setTotalItems(response.data.total);
          setTotalPages(response.data.last_page);
          setCurrentPage(response.data.current_page);
          setHasPreviousPage(response.data.current_page > 1);
          setHasNextPage(response.data.current_page < response.data.last_page);
        }
      } catch (error: any) {
        console.error("Error fetching usage data:", error);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, perPage]
  );

  return (
    <UsageAnalyticsContext.Provider
      value={{
        usageData,
        totalPages,
        currentPage,
        perPage,
        totalItems,
        hasPreviousPage,
        hasNextPage,
        loading,
        fetchUsageData,
        setPerPage,
      }}
    >
      {children}
    </UsageAnalyticsContext.Provider>
  );
};

export const useUsageAnalytics = () => {
  const context = useContext(UsageAnalyticsContext);
  if (!context) {
    throw new Error("useUsageAnalytics must be used within an UsageAnalyticsProvider");
  }
  return context;
};
